import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { log } from '@/lib/log';

const WorkspaceContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WorkspaceProvider({ children }) {
    const { user, session } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [currentWorkspace, setCurrentWorkspace] = useState(null);
    const [loading, setLoading] = useState(false);
    const [accountInfo, setAccountInfo] = useState({ is_owner: false, is_super_admin: false, plan: null, usage: null });

    const fetchWorkspaces = useCallback(async () => {
        if (!user) {
            setWorkspaces([]); setCurrentWorkspace(null);
            return;
        }
        setLoading(true);
        const { data: memberRows, error } = await supabase
            .from('workspace_members')
            .select('workspace_id, role, is_admin, workspaces:workspace_id(id,name,slug,industry,country,is_training,logo_url)')
            .eq('user_id', user.id);

        if (error) {
            log.warn('fetchWorkspaces error', error);
        }
        const list = (memberRows || []).map((m) => ({
            ...m.workspaces,
            role: m.role,
            is_admin: m.is_admin,
        })).filter((w) => w && w.id);
        setWorkspaces(list);

        const savedId = localStorage.getItem('avp_workspace_id');
        const found = list.find((w) => w.id === savedId) || list[0] || null;
        setCurrentWorkspace(found);
        if (found) localStorage.setItem('avp_workspace_id', found.id);
        setLoading(false);
    }, [user]);

    useEffect(() => { fetchWorkspaces(); }, [fetchWorkspaces]);

    const refreshAccount = useCallback(async () => {
        if (!session?.access_token) {
            setAccountInfo({ is_owner: false, is_super_admin: false, plan: null, usage: null });
            return;
        }
        try {
            const { data } = await axios.get(`${API}/account-plans/me`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            setAccountInfo({
                is_owner: !!data.is_owner,
                is_super_admin: !!data.is_super_admin,
                plan: data.plan || null,
                usage: data.usage || null,
                days_left: data.days_left,
                overage: data.overage,
            });
        } catch (e) {
            // Silent: keep defaults so the UI degrades gracefully (no extra buttons shown).
            setAccountInfo({ is_owner: false, is_super_admin: false, plan: null, usage: null });
        }
    }, [session?.access_token]);

    useEffect(() => { refreshAccount(); }, [refreshAccount]);

    const selectWorkspace = (ws) => {
        setCurrentWorkspace(ws);
        localStorage.setItem('avp_workspace_id', ws.id);
    };

    const createWorkspace = async ({ name, country, industry }) => {
        if (!user || !session?.access_token) return { error: { message: 'Not authenticated' } };
        try {
            const { data } = await axios.post(
                `${API}/workspaces/create`,
                { name, country, industry },
                { headers: { Authorization: `Bearer ${session.access_token}` } }
            );
            await fetchWorkspaces();
            return { data };
        } catch (err) {
            return { error: { message: err.response?.data?.detail || err.message } };
        }
    };

    return (
        <WorkspaceContext.Provider value={{ workspaces, currentWorkspace, loading, selectWorkspace, createWorkspace, refresh: fetchWorkspaces, accountInfo, refreshAccount }}>
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
    return ctx;
}
